import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LeadCaptureModal from '../../components/feature/LeadCaptureModal';
import BlogSection from './components/BlogSection';
import { supabase } from '../../utils/supabase';

interface Testimonial {
  id: string;
  name: string;
  company: string;
  role: string;
  content: string;
  rating: number;
  image_url: string;
}

export default function HomePage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_published', true)
        .order('order_position', { ascending: true })
        .limit(3);

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Erro ao buscar depoimentos:', error);
    }
  };

  const features = [
    {
      icon: 'ri-file-text-line',
      title: 'Emissão de CT-e e MDF-e',
      description: 'Sistema para emitir CT-e e MDF-e online de forma rápida e segura, com conformidade fiscal garantida',
      benefits: ['Emissor de CT-e integrado', 'Emissão de MDF-e automática', 'Programa para emissão de CT-e completo'],
      link: '/funcionalidades/cte-mdfe',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Controle Financeiro',
      description: 'Software de gestão de transportes com controle completo de custos, despesas e fluxo de caixa',
      benefits: ['Controle por veículo', 'Gestão de orçamentos', 'Visão estratégica'],
      link: '/funcionalidades/financeiro',
    },
    {
      icon: 'ri-archive-line',
      title: 'Gestão de Estoque',
      description: 'Sistema completo para transportadora com controle total de peças, insumos e custos de manutenção',
      benefits: ['Evite perdas', 'Controle de custos', 'Organização total'],
      link: '/funcionalidades/estoque',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Cadastro de Motoristas',
      description: 'Software para transportadora com gestão completa de documentação e histórico operacional',
      benefits: ['Documentação em dia', 'Controle de CNH', 'Histórico completo'],
      link: '/funcionalidades/motorista',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Relatórios Gerenciais',
      description: 'Sistema para gestão de frota com indicadores estratégicos para tomada de decisão',
      benefits: ['Rentabilidade por veículo', 'Análise de custos', 'KPIs em tempo real'],
      link: '/funcionalidades/relatorios',
    },
    {
      icon: 'ri-cloud-line',
      title: '100% na Nuvem',
      description: 'Melhor software para transportadora, acesse de qualquer lugar, a qualquer momento',
      benefits: ['Segurança de dados', 'Backup automático', 'Atualizações constantes'],
      link: '/funcionalidades',
    },
  ];

  const plans = [
    {
      name: 'Básico',
      price: 'R$ 59,90',
      period: '/mês',
      description: 'Transportadoras que já estão iniciando estruturadas',
      limit: 'Limitado a 10 CT-e',
      features: [
        'Cadastros',
        'Controle de estoque',
        'Emissor de CT-e integrado com SEFAZ',
        'Cadastro de motoristas',
        'SPED Fiscal ICMS',
        'Suporte especializado',
      ],
      highlighted: false,
    },
    {
      name: 'Essencial',
      price: 'R$ 129,90',
      period: '/mês',
      description: 'Transportadora em crescimento com gestão completa',
      limit: 'Emissão de CT-e e MDF-e ilimitada',
      features: [
        'Tudo do Básico',
        'Emissão ilimitada de documentos fiscais',
        'Entrada documentos por XML',
      ],
      highlighted: true,
      badge: 'Mais Popular',
    },
    {
      name: 'Gestão',
      price: 'R$ 169,90',
      period: '/mês',
      description: 'Transportadora que precisa de controle financeiro completo além do operacional',
      limit: 'Emissão de CT-e e MDF-e ilimitada',
      features: [
        'Tudo do Essencial',
        'Controle financeiro (contas a pagar e receber)',
        'Fluxo de caixa',
        'Conciliação financeira',
      ],
      highlighted: false,
    },
    {
      name: 'Personalizado',
      price: 'Monte seu combo',
      period: '',
      description: 'Quem quer adicionar recursos específicos ao Essencial ou Gestão',
      limit: 'Adicione apenas o que você precisa',
      features: [
        'Emissão de Boletos - cobrança facilitada',
        'SPED Contribuições (Pis e Cofins)',
        'Pedido de compra',
        'Integração com PIX',
        'Integração com TEF',
      ],
      highlighted: false,
      customLabel: 'Módulos adicionais disponíveis:',
    },
  ];

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(contactForm).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d66as5k8e72clamqhcf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/8f96309193033dd1ac16d4b4c3534c1c.jpg)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-secondary/20 backdrop-blur-sm border border-secondary/30 rounded-full mb-4 sm:mb-6">
            <i className="ri-award-line text-secondary mr-2 text-sm sm:text-base"></i>
            <span className="text-white text-xs sm:text-sm font-medium">Sistema para transportadora líder em gestão</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
            Sistema para Transportadora<br className="hidden sm:block" />
            <span className="sm:hidden"> </span>com Emissão de CT-e Online
          </h1>

          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-neutral-200 mb-6 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2">
            Software completo para gestão de transportes: emita CT-e e MDF-e, controle financeiro, gestão de frota e muito mais. O melhor programa para transportadora pequena e média.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-secondary text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-secondary-600 transition-all shadow-hover whitespace-nowrap cursor-pointer"
            >
              Teste Grátis por 14 Dias
            </button>
            <Link
              to="/funcionalidades"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap text-center"
            >
              Conheça o Sistema
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 lg:gap-8 text-white px-4">
            <div className="flex items-center space-x-2">
              <i className="ri-shield-check-line text-xl sm:text-2xl text-secondary"></i>
              <span className="text-xs sm:text-sm">Emissor de CT-e Certificado</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-cloud-line text-xl sm:text-2xl text-secondary"></i>
              <span className="text-xs sm:text-sm">100% na Nuvem</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-customer-service-2-line text-xl sm:text-2xl text-secondary"></i>
              <span className="text-xs sm:text-sm">Suporte Especializado</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4">
              O Sistema Completo para Transportadora que Você Precisa
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed px-2">
              O <strong>TG WEB</strong> é um <strong>software para transportadora</strong> desenvolvido especialmente para empresas de transporte de cargas. 
              Nosso <strong>sistema para emissão de CT-e</strong> e <strong>MDF-e online</strong> oferece controle total da sua operação, desde a <strong>emissão de CT-e</strong> até a 
              gestão financeira e de frota. Se você busca um <strong>programa para transportadora</strong> completo e fácil de usar, o TG WEB é a solução ideal.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-xl mx-auto mb-4">
                <i className="ri-file-check-line text-3xl text-primary"></i>
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Emissor de CT-e Completo</h3>
              <p className="text-neutral-600">
                <strong>Sistema para emitir CT-e</strong> e <strong>MDF-e</strong> com validação automática e integração com SEFAZ. 
                O melhor <strong>emissor de CT-e online</strong> do mercado.
              </p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-secondary/10 rounded-xl mx-auto mb-4">
                <i className="ri-truck-line text-3xl text-secondary"></i>
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Gestão de Frota Inteligente</h3>
              <p className="text-neutral-600">
                <strong>Sistema para gestão de frota</strong> com controle de veículos, motoristas, manutenções e custos operacionais. 
                Ideal para <strong>transportadora pequena</strong> e média.
              </p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-xl mx-auto mb-4">
                <i className="ri-line-chart-line text-3xl text-primary"></i>
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Software de Gestão Completo</h3>
              <p className="text-neutral-600">
                <strong>Software de gestão de transportes</strong> com controle financeiro, estoque, relatórios gerenciais e muito mais. 
                O <strong>sistema completo para transportadora de cargas</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-neutral-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4">
              Funcionalidades do Sistema para Transportadora
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed px-2">
              Tudo que você precisa em um único <strong>software para transportadora</strong>: desde a <strong>emissão de CT-e online</strong> até relatórios gerenciais completos
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className="group bg-white p-8 rounded-2xl shadow-card hover:shadow-hover transition-all cursor-pointer border border-neutral-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className={`${feature.icon} text-3xl text-primary`}></i>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-neutral-600 mb-4 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center text-sm text-neutral-700">
                      <i className="ri-check-line text-secondary mr-2"></i>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center text-primary font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Saiba mais
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4">
              Planos do Sistema para Transportadora
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed px-2">
              Escolha o plano ideal do nosso <strong>software para transportadora</strong> de acordo com as necessidades do seu negócio
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-6 sm:p-8 transition-all ${
                  plan.highlighted
                    ? 'border-2 border-secondary shadow-hover sm:scale-105'
                    : 'border border-neutral-200 shadow-card hover:shadow-hover'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-secondary text-white text-sm font-semibold rounded-full whitespace-nowrap">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-neutral-600 mb-4 min-h-[48px]">{plan.description}</p>
                  <div className="flex items-end justify-center mb-2">
                    <span className="text-4xl font-bold text-neutral-900">{plan.price}</span>
                    {plan.period && <span className="text-neutral-600 ml-1">{plan.period}</span>}
                  </div>
                  {plan.limit && (
                    <p className="text-xs text-secondary font-semibold">{plan.limit}</p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.customLabel && (
                    <li className="text-sm font-semibold text-neutral-800 mb-2">
                      {plan.customLabel}
                    </li>
                  )}
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm text-neutral-700">
                      <i className="ri-check-line text-secondary mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className={`w-full py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    plan.highlighted
                      ? 'bg-secondary text-white hover:bg-secondary-600 shadow-soft'
                      : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200'
                  }`}
                >
                  Falar com consultor
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/planos"
              className="inline-flex items-center text-primary font-semibold hover:text-primary-600 transition-colors"
            >
              Ver comparação completa de planos
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-24 bg-neutral-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4">
              Transportadoras que Confiam no TG WEB
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed px-2">
              Empresas que já transformaram sua gestão com nosso <strong>sistema para transportadora</strong>
            </p>
          </div>

          {testimonials.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white p-8 rounded-2xl shadow-card">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                    ))}
                  </div>
                  <p className="text-neutral-700 mb-6 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center">
                    <img
                      src={testimonial.image_url}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold text-neutral-900">{testimonial.name}</p>
                      <p className="text-sm text-neutral-600">{testimonial.role}</p>
                      <p className="text-sm text-neutral-500">{testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="ri-chat-quote-line text-5xl text-neutral-300 mb-4"></i>
              <p className="text-neutral-500">Nenhum depoimento disponível no momento</p>
            </div>
          )}
        </div>
      </section>

      {/* Blog Section */}
      <BlogSection />

      {/* Contact Section */}
      <section id="contato" className="py-16 sm:py-24 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4 sm:mb-6">
                Fale com Especialistas em Sistema para Transportadora
              </h2>
              <p className="text-base sm:text-lg text-neutral-600 mb-6 sm:mb-8 leading-relaxed">
                Nossa equipe está pronta para mostrar como nosso <strong>software para transportadora</strong> pode transformar sua gestão. 
                Tire suas dúvidas sobre <strong>emissão de CT-e</strong>, <strong>gestão de frota</strong> e todas as funcionalidades!
              </p>

              <div className="space-y-4 sm:space-y-6">
                <a
                  href="https://wa.me/5511999999999?text=Olá,%20gostaria%20de%20conhecer%20o%20sistema%20para%20transportadora%20TG%20WEB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-6 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-lg mr-4 group-hover:scale-110 transition-transform">
                    <i className="ri-whatsapp-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">WhatsApp</p>
                    <p className="text-neutral-600">(11) 99999-9999</p>
                  </div>
                </a>

                <div className="flex items-center p-6 bg-neutral-50 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-lg mr-4">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">Telefone</p>
                    <p className="text-neutral-600">(11) 4002-8922</p>
                  </div>
                </div>

                <div className="flex items-center p-6 bg-neutral-50 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-lg mr-4">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">Email</p>
                    <p className="text-neutral-600">contato@tgweb.com.br</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-neutral-50 p-6 sm:p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                Solicite uma Demonstração
              </h3>

              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-semibold text-neutral-900 mb-2">Mensagem enviada!</h4>
                  <p className="text-neutral-600">Entraremos em contato em breve.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} data-readdy-form id="contact-form">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-neutral-700 mb-1">
                        Nome *
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Seu nome"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-neutral-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="seu@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-neutral-700 mb-1">
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        required
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="(11) 99999-9999"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-neutral-700 mb-1">
                        Mensagem *
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                        placeholder="Como podemos ajudar?"
                      ></textarea>
                      <p className="text-xs text-neutral-500 mt-1">
                        {contactForm.message.length}/500 caracteres
                      </p>
                    </div>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">Erro ao enviar. Tente novamente.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full mt-6 px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-600 transition-all shadow-soft hover:shadow-hover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Enviando...' : 'Solicitar Demonstração'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-primary via-primary-700 to-primary-900 text-white">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            Experimente o Melhor Sistema para Transportadora
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-100 mb-6 sm:mb-10 leading-relaxed px-2">
            Junte-se a centenas de transportadoras que já aumentaram sua eficiência com nosso <strong>software de gestão de transportes</strong>. 
            Teste grátis por 14 dias o <strong>emissor de CT-e</strong> e todas as funcionalidades!
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-secondary text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-secondary-600 transition-all shadow-hover whitespace-nowrap cursor-pointer"
          >
            Começar Teste Grátis - 14 Dias
          </button>
        </div>
      </section>

      {/* Internal Links Section - SEO */}
      <section className="py-12 sm:py-16 bg-neutral-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Funcionalidades */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-neutral-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                <i className="ri-apps-line text-2xl text-primary"></i>
              </div>
              <h4 className="text-lg font-bold text-neutral-900 mb-3">
                <Link to="/funcionalidades" className="hover:text-primary transition-colors">
                  Funcionalidades do Sistema
                </Link>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/funcionalidades/cte-mdfe" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Emissão de CT-e e MDF-e
                  </Link>
                </li>
                <li>
                  <Link to="/funcionalidades/financeiro" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Controle Financeiro
                  </Link>
                </li>
                <li>
                  <Link to="/funcionalidades/estoque" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Gestão de Estoque
                  </Link>
                </li>
                <li>
                  <Link to="/funcionalidades/motorista" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Cadastro de Motoristas
                  </Link>
                </li>
                <li>
                  <Link to="/funcionalidades/relatorios" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Relatórios Gerenciais
                  </Link>
                </li>
              </ul>
            </div>

            {/* Recursos */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-neutral-100">
              <div className="w-12 h-12 flex items-center justify-center bg-secondary/10 rounded-lg mb-4">
                <i className="ri-book-open-line text-2xl text-secondary"></i>
              </div>
              <h4 className="text-lg font-bold text-neutral-900 mb-3">
                <Link to="/blog" className="hover:text-primary transition-colors">
                  Recursos e Conteúdos
                </Link>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/blog" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Blog sobre Gestão de Transportes
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Perguntas Frequentes
                  </Link>
                </li>
                <li>
                  <Link to="/sistema-emissao-cte" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Guia: Emissor de CT-e Online
                  </Link>
                </li>
                <li>
                  <Link to="/planos" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Planos e Preços
                  </Link>
                </li>
                <li>
                  <Link to="/sitemap" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Mapa do Site
                  </Link>
                </li>
              </ul>
            </div>

            {/* Soluções */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-neutral-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                <i className="ri-truck-line text-2xl text-primary"></i>
              </div>
              <h4 className="text-lg font-bold text-neutral-900 mb-3">
                <Link to="/planos" className="hover:text-primary transition-colors">
                  Soluções por Porte
                </Link>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/planos" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Sistema para Transportadora Pequena
                  </Link>
                </li>
                <li>
                  <Link to="/planos" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Software para Transportadora Média
                  </Link>
                </li>
                <li>
                  <Link to="/planos" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Gestão de Grandes Frotas
                  </Link>
                </li>
                <li>
                  <Link to="/funcionalidades" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Transportadora de Cargas
                  </Link>
                </li>
                <li>
                  <Link to="/sistema-emissao-cte" className="text-neutral-600 hover:text-primary transition-colors flex items-center">
                    <i className="ri-arrow-right-s-line mr-1"></i>
                    Emissão de CT-e e MDF-e
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
